import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Badge, Spinner, Alert, ProgressBar, Modal } from 'react-bootstrap';
import { 
  getTradeById, 
  sendPayment, 
  confirmPayment, 
  sendCrypto, 
  confirmCrypto, 
  releaseEscrow,
  autoConfirmAndRelease,
  refreshWalletData,
  createRating,
  type Trade 
} from '../../services/p2pAPI';
import './TradeDetails.css';
import IconicHeader from '../updated/shared/IconicHeader';
import P2PNavigation from './P2PNavigation';

const TradeDetails: React.FC = () => {
  const { tradeId } = useParams<{ tradeId: string }>();
  const navigate = useNavigate();
  const [trade, setTrade] = useState<Trade | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string>('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [walletRefreshed, setWalletRefreshed] = useState(false);

  useEffect(() => {
    if (tradeId) {
      loadTradeDetails();
    }
  }, [tradeId]);

  // Apply theme class based on userType (CaptainBee = orange, normal = green)
  // Match the logic used in IconicHeader: if userType !== 'Indexx Exchange', it's CaptainBee
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const userLogged = localStorage.getItem('userlogged');
    
    // Determine theme class based on userType or userlogged
    // Match IconicHeader logic: userType !== 'Indexx Exchange' means CaptainBee
    let themeClass = 'theme-normal'; // Default to green
    
    if (userType && userType !== 'Indexx Exchange') {
      // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'CaptainBee' || userLogged === 'captain') {
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
      themeClass = 'theme-honeyb'; // Orange for HoneyBee
    } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
      themeClass = 'theme-normal'; // Green for normal users
    }
    
    // Apply theme class to document body and root
    document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.body.classList.add(themeClass);
    document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.documentElement.classList.add(themeClass);
    
    // Also apply to root element to ensure CSS variables are available
    const root = document.getElementById('root');
    if (root) {
      root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      root.classList.add(themeClass);
    }
    
    // Cleanup on unmount
    return () => {
      // Don't remove theme class on unmount as other components might need it
    };
  }, []);

  const loadTradeDetails = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getTradeById(tradeId!);
      
      // Safely extract trade data from response
      const updatedTrade = response?.data || response;
      
      if (!updatedTrade || !updatedTrade.id) {
        throw new Error('Invalid trade data received');
      }
      
      setTrade(updatedTrade);

      // If trade is completed, ensure wallets refresh once
      if (updatedTrade?.status === 'completed' && !walletRefreshed) {
        await refreshWalletsAndDashboard(updatedTrade);
        setWalletRefreshed(true);
      }

    } catch (error: any) {
      console.error('Failed to load trade details:', error);
      const errorMessage = error?.message || 
                          error?.response?.data?.message || 
                          error?.response?.data?.error ||
                          'Failed to load trade details';
      setError(errorMessage);
      setTrade(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to refresh wallets for both parties and trigger dashboard refresh
  const refreshWalletsAndDashboard = async (completedTrade: Trade) => {
    try {
      console.log('Refreshing wallets for completed trade...');
      
      // Refresh wallet data for buyer
      if (completedTrade.buyerEmail) {
        try {
          await refreshWalletData(completedTrade.buyerEmail);
          console.log('Wallet refreshed for buyer:', completedTrade.buyerEmail);
        } catch (buyerError) {
          console.error('Failed to refresh buyer wallet:', buyerError);
        }
      }
      
      // Refresh wallet data for seller
      if (completedTrade.sellerEmail) {
        try {
          await refreshWalletData(completedTrade.sellerEmail);
          console.log('Wallet refreshed for seller:', completedTrade.sellerEmail);
        } catch (sellerError) {
          console.error('Failed to refresh seller wallet:', sellerError);
        }
      }
      
      // Mark trade as completed in session to refresh on next load
      sessionStorage.setItem(`trade_completed_${tradeId}`, 'true');
      
      // Trigger dashboard refresh by storing flag
      localStorage.setItem('p2p_dashboard_refresh_needed', Date.now().toString());
      localStorage.setItem('p2p_trade_created', Date.now().toString()); // Also trigger offers refresh
      
      console.log('All wallets and dashboard refresh flags set');
    } catch (error) {
      console.error('Error refreshing wallets:', error);
    }
  };

  const handleAction = async (action: string) => {
    if (!trade) return;

    try {
      setActionLoading(action);
      
      switch (action) {
        case 'send-payment':
          await sendPayment(trade.id, 'Payment sent by user');
          break;
        case 'confirm-payment':
          await confirmPayment(trade.id);
          break;
        case 'send-crypto':
          await sendCrypto(trade.id, 'auto-generated-hash-' + Date.now());
          break;
        case 'confirm-crypto':
          await confirmCrypto(trade.id);
          break;
        case 'release':
          await releaseEscrow(trade.id);
          break;
        case 'auto-confirm':
          console.log('Auto-confirming and releasing escrow...');
          const result = await autoConfirmAndRelease(trade.id);
          console.log('Auto-confirm result:', result);
          alert('Trade completed automatically! Payment and crypto confirmed, escrow released.');
          
          // Mark trade as completed for refresh
          sessionStorage.setItem(`trade_completed_${trade.id}`, 'true');
          break;
      }
      
      // Reload trade details to get updated status
      await loadTradeDetails();
      
      // Check if trade is now completed after the action
      setTimeout(async () => {
        try {
          const updatedResponse = await getTradeById(trade.id);
          // Safely extract trade data from response (same pattern as loadTradeDetails)
          const updatedTrade = updatedResponse?.data || updatedResponse;
          
          if (!updatedTrade || !updatedTrade.id) {
            console.error('Invalid trade data received in status check');
            return;
          }
          
          if (updatedTrade?.status === 'completed') {
            console.log('Trade completed, refreshing wallets and dashboard...');
            await refreshWalletsAndDashboard(updatedTrade);
            
            // If trade is completed and user is buyer, show rating modal
            const currentUser = localStorage.getItem('user');
            if (currentUser === updatedTrade.buyerEmail) {
              setTimeout(() => {
                setShowRatingModal(true);
              }, 1000);
            }
            setWalletRefreshed(true);
          } else if (action === 'auto-confirm') {
            // For auto-confirm, always refresh even if status check fails
            await refreshWalletsAndDashboard(trade);
            
            const currentUser = localStorage.getItem('user');
            if (currentUser === trade.buyerEmail) {
              setTimeout(() => {
                setShowRatingModal(true);
              }, 1000);
            }
          }
        } catch (checkError) {
          console.error('Error checking trade completion:', checkError);
        }
      }, 1500);
    } catch (error: any) {
      console.error(`Failed to ${action}:`, error);
      alert(`Failed to ${action}: ${error.message}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleSubmitRating = async () => {
    if (!trade || rating === 0) return;

    try {
      setRatingSubmitting(true);
      const currentUser = localStorage.getItem('user');
      const adminEmail = trade.sellerEmail; // Admin is the seller who created the offer
      
      await createRating({
        toUser: adminEmail,
        rating: rating,
        comment: ratingComment,
        tradeId: trade.id,
      });
      
      setHasRated(true);
      setShowRatingModal(false);
      alert('Thank you for your rating!');
      
      // Reset rating state
      setRating(0);
      setRatingComment('');
    } catch (error: any) {
      console.error('Failed to submit rating:', error);
      alert(`Failed to submit rating: ${error.message || 'Unknown error'}`);
    } finally {
      setRatingSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'success', text: 'Pending Payment' },
      paid: { variant: 'info', text: 'Payment Received' },
      crypto_sent: { variant: 'primary', text: 'Crypto Sent' },
      completed: { variant: 'success', text: 'Completed' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      disputed: { variant: 'danger', text: 'Disputed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { 
      variant: 'secondary', 
      text: status 
    };

    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getTradeProgress = (status: string) => {
    const progressMap = {
      pending: 25,
      paid: 50,
      crypto_sent: 75,
      completed: 100,
      cancelled: 0,
      disputed: 0,
    };
    return progressMap[status as keyof typeof progressMap] || 0;
  };

  const getTradeRole = () => {
    if (!trade) return null;
    const currentUser = localStorage.getItem('user');
    if (trade.buyerEmail === currentUser) {
      return { role: 'Buyer', variant: 'success' };
    } else if (trade.sellerEmail === currentUser) {
      return { role: 'Seller', variant: 'info' };
    }
    return null;
  };

  const getActionButtons = () => {
    if (!trade) return [];

    const currentUser = localStorage.getItem('user');
    const isBuyer = trade.buyerEmail === currentUser;
    const isSeller = trade.sellerEmail === currentUser;
    const buttons = [];

    // Send Payment button removed

    if (trade.status === 'paid' && isSeller) {
      buttons.push(
        <Button 
          key="send-crypto" 
          variant="primary" 
          size="sm"
          onClick={() => handleAction('send-crypto')}
          disabled={actionLoading === 'send-crypto'}
          className="w-100"
        >
          {actionLoading === 'send-crypto' ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="fas fa-coins me-2"></i>
          )}
          Send Crypto
        </Button>
      );
    }

    if (trade.status === 'crypto_sent' && isBuyer) {
      buttons.push(
        <Button 
          key="confirm" 
          variant="success" 
          size="sm"
          onClick={() => handleAction('confirm-crypto')}
          disabled={actionLoading === 'confirm-crypto'}
          className="w-100"
        >
          {actionLoading === 'confirm-crypto' ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="fas fa-check me-2"></i>
          )}
          Confirm Receipt
        </Button>
      );
      
      // Add auto-confirm button for seamless completion
      buttons.push(
        <Button 
          key="auto-confirm" 
          variant="warning" 
          size="sm"
          onClick={() => handleAction('auto-confirm')}
          disabled={actionLoading === 'auto-confirm'}
          className="w-100"
        >
          {actionLoading === 'auto-confirm' ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="fas fa-magic me-2"></i>
          )}
          Auto Complete Trade
        </Button>
      );
    }

    return buttons;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="trade-details-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading trade details...</p>
      </div>
    );
  }

  if (error || !trade) {
    return (
      <div className="binance-trade-details">
        <div className="error-alert">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error || 'Trade not found'}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <Link to="/p2p/trades">
                <Button variant="outline-danger">
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Trades
                </Button>
              </Link>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  const roleInfo = getTradeRole();
  const progress = getTradeProgress(trade.status);

  return (
    <div className="binance-trade-details">
      <IconicHeader selectedTab="P2P" onChange={() => {}} />
      <P2PNavigation />
      <div className="p2p-content-container">
      {/* Binance-style Header */}
      <div className="binance-header">
        <div className="header-content">
          <h1>Trade Details</h1>
        </div>
        <div className="header-actions">
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={() => navigate('/p2p/trades')}
          >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Trades
            </Button>
        </div>
      </div>

      {/* Trade Progress */}
      <div className="trade-progress-section">
        <div className="progress-header">
          <h4>Trade Progress</h4>
          <Badge bg="success">{progress}% Complete</Badge>
        </div>
        <ProgressBar 
          now={progress} 
          variant="success" 
          className="trade-progress-bar"
        />
        <div className="progress-steps">
          <div className={`step ${progress >= 25 ? 'completed' : ''}`}>
            <i className="fas fa-credit-card"></i>
            <span>Payment</span>
          </div>
          <div className={`step ${progress >= 50 ? 'completed' : ''}`}>
            <i className="fas fa-check-circle"></i>
            <span>Confirmed</span>
          </div>
          <div className={`step ${progress >= 75 ? 'completed' : ''}`}>
            <i className="fas fa-coins"></i>
            <span>Crypto Sent</span>
          </div>
          <div className={`step ${progress >= 100 ? 'completed' : ''}`}>
            <i className="fas fa-handshake"></i>
            <span>Completed</span>
          </div>
        </div>
      </div>

      {/* Combined Trade Card */}
      <div className="trade-combined-section">
        <Card className="trade-combined-card">
          <Card.Header className="trade-combined-header">
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <h4 className="mb-0">I am joining the trade</h4>
              </div>
              <div className="d-flex align-items-center gap-3">
                {getStatusBadge(trade.status)}
                {roleInfo && <Badge bg={roleInfo.variant}>{roleInfo.role}</Badge>}
              </div>
            </div>
              </Card.Header>
          <Card.Body className="trade-combined-body">
                <Row>
              {/* Left Column: Trade Information */}
              <Col lg={5}>
                <div className="combined-section">
                  <h6 className="section-title">
                    <i className="fas fa-info-circle me-2"></i>
                    Trade Information
                  </h6>
                  <div className="info-grid">
                    <div className="info-item">
                      <label><i className="fas fa-hashtag me-1"></i> Offer ID</label>
                      <div className="info-value">{trade.offerId || 'N/A'}</div>
                      </div>
                    <div className="info-item">
                      <label><i className="fas fa-coins me-1"></i> Crypto Type</label>
                      <div className="info-value">{trade.cryptoType}</div>
                    </div>
                    <div className="info-item">
                      <label><i className="fas fa-chart-line me-1"></i> Amount</label>
                      <div className="info-value">{trade.amount} {trade.cryptoType}</div>
                    </div>
                    <div className="info-item">
                      <label><i className="fas fa-dollar-sign me-1"></i> Price per Unit</label>
                      <div className="info-value">{formatCurrency(trade.price)}</div>
                    </div>
                    <div className="info-item">
                      <label><i className="fas fa-calculator me-1"></i> Total Value</label>
                      <div className="info-value highlight-value">{formatCurrency(trade.amount * trade.price)}</div>
                    </div>
                    <div className="info-item">
                      <label><i className="fas fa-calendar me-1"></i> Created</label>
                      <div className="info-value">{formatDate(trade.createdAt)}</div>
                    </div>
                    <div className="info-item">
                      <label><i className="fas fa-clock me-1"></i> Last Updated</label>
                      <div className="info-value">{formatDate(trade.updatedAt)}</div>
                    </div>
                  </div>
                </div>
          </Col>
          
              {/* Middle Column: Trade Timeline */}
              <Col lg={4}>
                <div className="combined-section">
                  <h6 className="section-title">
                    <i className="fas fa-history me-2"></i>
                    Trade Timeline
                  </h6>
                  <div className="timeline-compact">
                    <div className="timeline-item-compact">
                      <div className="timeline-marker-compact completed"></div>
                      <div className="timeline-content-compact">
                        <h6>Trade Created</h6>
                        <small>{formatDate(trade.createdAt)}</small>
                      </div>
                    </div>
                    
                    {trade.status !== 'pending' && (
                      <div className="timeline-item-compact">
                        <div className="timeline-marker-compact completed"></div>
                        <div className="timeline-content-compact">
                          <h6>Payment Sent</h6>
                          <small>{formatDate(trade.updatedAt)}</small>
                        </div>
                      </div>
                    )}
                    
                    {['crypto_sent', 'completed'].includes(trade.status) && (
                      <div className="timeline-item-compact">
                        <div className="timeline-marker-compact completed"></div>
                        <div className="timeline-content-compact">
                          <h6>Crypto Sent</h6>
                          <small>{formatDate(trade.updatedAt)}</small>
                        </div>
                      </div>
                    )}
                    
                    {trade.status === 'completed' && (
                      <div className="timeline-item-compact">
                        <div className="timeline-marker-compact completed"></div>
                        <div className="timeline-content-compact">
                          <h6>Trade Completed</h6>
                          <small>{formatDate(trade.updatedAt)}</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Right Column: Actions */}
              <Col lg={3}>
                <div className="combined-section">
                  <h6 className="section-title">
                    <i className="fas fa-bolt me-2"></i>
                    Actions
                  </h6>
                  <div className="action-buttons-compact">
                    {getActionButtons().length > 0 ? (
                      getActionButtons().map((btn, idx) => (
                        <div key={idx} className="mb-2">
                          {btn}
                        </div>
                      ))
                    ) : (
                      <div className="no-actions">
                        <i className="fas fa-info-circle"></i>
                        <p>No actions available at this time</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Rating Button for Completed Trades */}
                  {trade.status === 'completed' && !hasRated && trade.buyerEmail === localStorage.getItem('user') && (
                    <>
                      <hr className="section-divider" />
                      <Button 
                        variant="warning" 
                        className="w-100 mb-2 rating-btn"
                        size="sm"
                        onClick={() => setShowRatingModal(true)}
                      >
                        <i className="fas fa-star me-2"></i>
                        Rate Trade Experience
                      </Button>
                    </>
                  )}
                  
                  <hr className="section-divider" />
                  
                  <div className="additional-actions-compact">
                  <Link to={`/p2p/disputes?tradeId=${trade.id}`}>
                    <Button 
                      variant="outline-warning" 
                      className="w-100 mb-2"
                        size="sm"
                    >
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Create Dispute
                    </Button>
                  </Link>
                </div>
                </div>
              </Col>
            </Row>
              </Card.Body>
            </Card>
      </div>

      {/* Rating Modal */}
      <Modal 
        show={showRatingModal} 
        onHide={() => !ratingSubmitting && setShowRatingModal(false)} 
        centered
        className="rating-modal"
      >
        <Modal.Header className="rating-modal-header" closeButton={!ratingSubmitting}>
          <Modal.Title>
            <i className="fas fa-star me-2"></i>
            Rate Your Trade Experience
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="rating-modal-body">
          <div className="rating-container">
            <p className="rating-subtitle">How would you rate your experience with this trade?</p>
            
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`star-btn ${star <= (ratingHover || rating) ? 'active' : ''}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setRatingHover(star)}
                  onMouseLeave={() => setRatingHover(0)}
                  disabled={ratingSubmitting}
                >
                  <i className={`fas fa-star ${star <= (ratingHover || rating) ? 'filled' : 'outline'}`}></i>
                </button>
              ))}
              </div>
              
            {rating > 0 && (
              <div className="rating-labels">
                <span className={`rating-label ${rating >= 1 ? 'show' : ''}`}>
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </span>
                </div>
              )}
              
            <div className="rating-comment-section">
              <label htmlFor="ratingComment" className="rating-comment-label">
                <i className="fas fa-comment me-2"></i>
                Additional Comments (Optional)
              </label>
              <textarea
                id="ratingComment"
                className="form-control rating-comment-textarea"
                rows={4}
                placeholder="Share your experience with this trade..."
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                disabled={ratingSubmitting}
              />
                  </div>
            
            <div className="rating-actions">
              <Button 
                variant="primary" 
                className="rating-submit-btn"
                onClick={handleSubmitRating}
                disabled={rating === 0 || ratingSubmitting}
              >
                {ratingSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-2"></i>
                    Submit Rating
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowRatingModal(false)}
                disabled={ratingSubmitting}
              >
                Cancel
              </Button>
            </div>
      </div>
        </Modal.Body>
      </Modal>

      {/* Chat Modal */}
      <Modal show={showChatModal} onHide={() => setShowChatModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Trade Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="chat-container">
            <div className="chat-messages">
              <div className="message">
                <div className="message-header">
                  <strong>System</strong>
                  <small>Just now</small>
                </div>
                <div className="message-content">
                  Trade chat will be implemented with real-time messaging
                </div>
              </div>
            </div>
            <div className="chat-input">
              <input
                type="text"
                placeholder="Type your message..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                className="form-control"
              />
              <Button variant="primary" className="ms-2">
                Send
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      </div>
    </div>
  );
};

export default TradeDetails;
